import { useEffect, useRef, useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { SITE_STATS } from '@/config/siteStats';
import PrefetchLink from '@/components/shared/PrefetchLink';

interface Stat {
  /** Final numeric target for the count-up. */
  value: number;
  /** Optional suffix appended to the rendered number, e.g. "+". */
  suffix?: string;
  label: string;
  /** Optional story page to link to from this stat. */
  to?: string;
}

const stats: Stat[] = [
  { value: SITE_STATS.yearsOfHumanHistory, label: 'Years of Human History', to: '/native-american-heritage' },
  { value: SITE_STATS.yearsSinceFounding, suffix: '+', label: 'Years Since 1880', to: '/ranch-history' },
  { value: SITE_STATS.peakAcres, label: "Acres at the Empire's Peak", to: '/captain-schreiner' },
  { value: SITE_STATS.longhornsTrailedToDodgeCity, label: 'Longhorns Trailed to Dodge City', to: '/texas-longhorn-heritage' },
  { value: SITE_STATS.archivalPhotos, suffix: '+', label: 'Archival Photos', to: '/historical-gallery' },
  { value: SITE_STATS.schreinerGenerations, label: 'Schreiner Generations', to: '/schreiner-dynasty' },
  { value: SITE_STATS.exoticSpecies, suffix: '+', label: 'Exotic Species', to: '/wildlife-heritage' },
];
const prefersReducedMotion = () =>

  typeof window !== 'undefined' &&
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/**
 * Self-contained stat counter. Each instance owns its IntersectionObserver,
 * its count-up rAF loop, a 1500ms fallback that snaps to the final value if
 * the observer never fires, and respects prefers-reduced-motion.
 *
 * To prevent CLS the cell renders a hidden ghost containing the final
 * formatted value so the column reserves the correct width up front.
 */
const StatCounter = ({ value, suffix = '', label, to }: Stat) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [display, setDisplay] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    let rafId = 0;
    let fallbackId: number | undefined;
    let didStart = false;

    const finalize = () => {
      setDisplay(value);
      setHasAnimated(true);
    };

    const start = () => {
      if (didStart) return;
      didStart = true;

      if (prefersReducedMotion()) {
        finalize();
        return;
      }

      const duration = 1200;
      const startTime = performance.now();
      const tick = (now: number) => {
        const elapsed = now - startTime;
        const t = Math.min(1, elapsed / duration);
        // easeOutCubic
        const eased = 1 - Math.pow(1 - t, 3);
        setDisplay(Math.round(eased * value));
        if (t < 1) {
          rafId = requestAnimationFrame(tick);
        } else {
          finalize();
        }
      };
      rafId = requestAnimationFrame(tick);
    };

    // Per-element observer with a generous threshold so partially-visible
    // cells (e.g. the rightmost column on narrow viewports) still trigger.
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            start();
            observer.disconnect();
            break;
          }
        }
      },
      { threshold: 0.25 },
    );
    observer.observe(node);

    // Some browsers report initial intersection on the next frame; poll
    // the bounding rect once on mount to cover "in-view on arrival".
    const rect = node.getBoundingClientRect();
    const inView =
      rect.top < (window.innerHeight || document.documentElement.clientHeight) &&
      rect.bottom > 0;
    if (inView) start();

    // Failsafe — if neither path triggered within 1500ms, snap to final.
    fallbackId = window.setTimeout(() => {
      if (!didStart) finalize();
    }, 1500);

    return () => {
      cancelAnimationFrame(rafId);
      if (fallbackId) clearTimeout(fallbackId);
      observer.disconnect();
    };
  }, [value]);

  return (
    <div ref={ref} className="text-center" style={{ opacity: 1 }}>
      <div
        className="font-display text-3xl lg:text-4xl font-normal text-brass tracking-wide mb-2 tabular-nums relative inline-block"
        aria-label={`${value.toLocaleString()}${suffix} ${label}`}
      >
        {/* Visible animated value */}
        <span aria-hidden="true">
          {display.toLocaleString()}
          {hasAnimated ? suffix : ''}
        </span>
        {/* Width reservation ghost — prevents CLS during count-up */}
        <span aria-hidden="true" className="invisible block h-0 overflow-hidden">
          {value.toLocaleString()}
          {suffix}
        </span>
      </div>
      <div className="font-sans text-xs tracking-widest uppercase text-hero-text/60 mb-2">
        {label}
      </div>
      {to && (
        <PrefetchLink
          to={to}
          className="inline-flex items-center gap-1 font-sans text-[10px] tracking-widest uppercase text-brass/70 hover:text-brass transition-colors group"
          aria-label={`Learn more about ${label}`}
        >
          Learn more
          <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
        </PrefetchLink>
      )}
    </div>
  );

};

const StatsStrip = () => {
  return (
    <section className="py-16 lg:py-20 bg-hero text-hero-text relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.02] bg-[radial-gradient(circle_at_50%_50%,_hsl(var(--hero-text))_1px,_transparent_1px)] bg-[length:24px_24px]" />
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12">
          {stats.map((stat) => (
            <StatCounter key={stat.label} {...stat} />
          ))}
        </div>
      </div>
    </section>
  );
};


export default StatsStrip;
